import { Timestamp } from "firebase/firestore";
import {
  CheckSize,
  Firm,
  GeographyFocus,
  ImportedInvestorEntry,
  MetadataPair,
} from "../types/ImportedInvestorEntry";
import { cleanCommaSplitStringList } from "./clean-comma-split-string-list";

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

/** Small, dependency-free UUID generator (crypto if available, else fallback). */
function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return (
    Date.now().toString(36) +
    Math.random().toString(36).substring(2, 8).toUpperCase()
  );
}

function removeBlanksFromArray(array: string[]): string[] {
  return array.filter((item) => !!item?.trim());
}

function dedupeGeographyFocus(
  geographyFocus: GeographyFocus | null,
): GeographyFocus | null {
  if (!geographyFocus) {
    return null;
  }

  const { cities, countries, regions } = geographyFocus;
  return {
    cities: cities ? Array.from(new Set(cities)) : [],
    countries: countries ? Array.from(new Set(countries)) : [],
    regions: regions ? Array.from(new Set(regions)) : [],
  };
}

/** Parse values such as "$25k", "1.2 M", "€3,500,000" → 25000 / 1200000 / 3500000 */
function parseCurrency(value: string): number | undefined {
  const cleaned = value.toLowerCase().replace(/[^0-9.kmb]/g, "");
  if (!cleaned) {
    return undefined;
  }

  let multiplier = 1;
  let numeric = cleaned;

  if (numeric.endsWith("k")) {
    multiplier = 1_000;
    numeric = numeric.slice(0, -1);
  } else if (numeric.endsWith("m")) {
    multiplier = 1_000_000;
    numeric = numeric.slice(0, -1);
  } else if (numeric.endsWith("b")) {
    multiplier = 1_000_000_000;
    numeric = numeric.slice(0, -1);
  }

  const num = parseFloat(numeric.replace(/,/g, ""));
  return isNaN(num) ? undefined : num * multiplier;
}

export type MappingParams = {
  sourceName?: string;
  sourceUrl?: string;
  importedByUserId: string;
  defaultStatus?: "active" | "rejected" | "pending";
};

/* ------------------------------------------------------------------ */
/* main mapping function                                              */
/* ------------------------------------------------------------------ */
/**
 * Converts one CSV row into our unified ImportedInvestorEntry.
 * It heuristically matches *similar* header names so it works for every file
 * listed in `data-processed/manifest.json`.
 */
export function mapCsvRowToImportedInvestorEntry(
  row: Record<string, string | undefined>,
  {
    sourceName,
    sourceUrl,
    importedByUserId,
    defaultStatus = "pending",
  }: MappingParams,
): ImportedInvestorEntry {
  if (!sourceName) {
    throw new Error("Source name is required");
  }

  /* bucket variables while we iterate over columns ----------------------- */
  const social: ImportedInvestorEntry["social"] = {
    linkedin: null,
    twitter: null,
    crunchbase: null,
    instagram: null,
    youtube: null,
    tiktok: null,
    other: [],
  };

  let primaryFirm: Firm | null = null;
  const geographyFocus: GeographyFocus = {};
  const checkSize: CheckSize = {};
  const stageFocus = new Set<string>();
  const sectorFocus = new Set<string>();
  const portfolioCompanies = new Set<string>();
  const otherMetadata: MetadataPair[] = [];

  let firstName: string | null = null;
  let lastName: string | null = null;
  let fullName = "";
  let title: string | null = null;
  let email: string | null = null;
  let phone: string | null = null;
  let notes: string | null = null;

  /* pass 1 – examine every column ---------------------------------------- */
  for (const [header, raw] of Object.entries(row)) {
    const value = (raw ?? "").trim();
    if (!value) {
      continue;
    }

    const key = header.toLowerCase();

    /* identity ---------------------------------------------------------- */
    if (
      [
        "vc / investor name",
        "investor name",
        "partner name",
        // "name",
        "investor",
      ].some((h) => key.includes(h))
    ) {
      fullName = value;
      continue;
    }
    if (key.includes("first name")) {
      firstName = value;
      continue;
    }
    if (key.includes("last name")) {
      lastName = value;
      continue;
    }
    if (key === "title" || key.endsWith("title")) {
      title = value;
      continue;
    }
    if (key.includes("email")) {
      email = value;
      continue;
    }
    if (key.includes("phone")) {
      phone = value;
      continue;
    }

    /* social ------------------------------------------------------------ */
    if (key.includes("linkedin")) {
      social.linkedin = value;
      continue;
    }
    if (key.includes("twitter")) {
      social.twitter = value;
      continue;
    }
    if (key.includes("crunchbase")) {
      social.crunchbase = value;
      continue;
    }
    if (key.includes("instagram")) {
      social.instagram = value;
      continue;
    }
    if (key.includes("youtube")) {
      social.youtube = value;
      continue;
    }
    if (key.includes("tiktok")) {
      social.tiktok = value;
      continue;
    }

    /* firm -------------------------------------------------------------- */
    if (
      [
        "primary firm",
        "firm",
        "organization name",
        "fund name",
        "vc name",
      ].some((h) => key.includes(h))
    ) {
      primaryFirm = { ...(primaryFirm ?? {}), name: value };
      continue;
    }

    /* thesis ------------------------------------------------------------ */
    if (key.includes("stage")) {
      value
        .split(/[,;/]+/)
        .map((s) => s.trim().toLowerCase())
        .forEach((s) => stageFocus.add(s));
      continue;
    }
    if (
      ["niches", "fund focus", "sector", "vertical", "industry focus"].some(
        (h) => key.includes(h),
      )
    ) {
      cleanCommaSplitStringList(
        value.split(/[,;/]+/).map((s) => s.trim().toLowerCase()),
      ).forEach((s) => sectorFocus.add(s));
      continue;
    }

    if (
      [
        "geographies investing in",
        "country",
        "countries",
        "geo",
        "location",
      ].some((h) => key.includes(h))
    ) {
      const parts = value.split(/[,;/]+/).map((s) => s.trim());
      geographyFocus.countries = [
        ...(geographyFocus.countries ?? []),
        ...parts,
      ];
      continue;
    }
    if (key.includes("city")) {
      const parts = value.split(/[,;/]+/).map((s) => s.trim());
      geographyFocus.cities = [...(geographyFocus.cities ?? []), ...parts];
      continue;
    }

    /* check sizes ------------------------------------------------------- */
    if (key.includes("min check size")) {
      const num = parseCurrency(value);
      if (num !== undefined) {
        checkSize.min = { value: num };
      }
      continue;
    }
    if (key.includes("max check size")) {
      const num = parseCurrency(value);
      if (num !== undefined) {
        checkSize.max = { value: num };
      }
      continue;
    }
    if (key.includes("check size bucket")) {
      checkSize.bucket = value;
      continue;
    }

    /* portfolio --------------------------------------------------------- */
    if (
      ["selected investments", "portfolio companies"].some((h) =>
        key.includes(h),
      )
    ) {
      value
        .split(/[,;/]+/)
        .map((s) => s.trim())
        .forEach((s) => portfolioCompanies.add(s));
      continue;
    }

    if (key.includes("notes")) {
      notes = value;
      continue;
    }

    /* fallback → record as raw metadata --------------------------------- */
    otherMetadata.push({ tag: header, value });
  }

  /* derive missing fullName from first/last ----------------------------- */
  if (!fullName) {
    fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  }

  /* assemble final object ----------------------------------------------- */
  const entry: ImportedInvestorEntry = {
    id: generateId(),
    searchableInvestorEntryId: null,

    /* identity */
    firstName,
    lastName,
    fullName,
    photos: [],

    /* contact */
    email,
    phone,
    social,

    /* firm / role */
    title,
    investorRole: null,
    primaryFirm,
    otherFirms: [],

    /* thesis */
    stageFocus: stageFocus.size
      ? removeBlanksFromArray(Array.from(stageFocus))
      : [],
    sectorFocus: sectorFocus.size
      ? removeBlanksFromArray(Array.from(sectorFocus))
      : [],
    geographyFocus: dedupeGeographyFocus(geographyFocus),
    checkSize: Object.keys(checkSize).length ? checkSize : null,
    leadsRounds: null,

    /* portfolio */
    selectedInvestments: [],
    portfolioCompanies: portfolioCompanies.size
      ? removeBlanksFromArray(Array.from(portfolioCompanies))
      : [],

    /* misc */
    tags: [],
    notes,
    otherMetadata: otherMetadata.length ? otherMetadata : [],

    /* import bookkeeping */
    sourceName,
    sourceUrl: sourceUrl ?? null,
    importedAt: Timestamp.now(),
    importedByUserId,

    /* lifecycle */
    status: defaultStatus,
    fieldStatuses: [],
    updatedAt: Timestamp.now(),
  };

  return entry;
}
